import _ from 'underscore';
import React, { useContext, useMemo } from 'react';
import { Popover,
  PopoverTrigger,
  PopoverSurface,
  mergeClasses,
  Badge, } from '@fluentui/react-components';
import { WarningRegular } from '@fluentui/react-icons';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { ScopeContext } from '@bingads-webui-react/scope-service';

import { getRoutePathById, allSiteMap } from '@bingads-webui-react/with-site-map';

import { ErrorCodeMapping, TrackingStatusComponentProps, I18n, GoalType } from '../types';
import { useTrackingStatusPopoverStyles } from './tracking-status-popover-styles';
import { errorcodeMapping, GoalDiagnosticsInfoErrorCode, healthyTrackingStatus, localizedParameters, needLocalizedPropName, trackingStatusMapping } from '../constants';
import { generateEventGoalFixMessage } from '../utils';
import { NavigationPopup } from './navigation-popup';

const TrackingStatusComponent = React.forwardRef<
HTMLDivElement,
Partial<TrackingStatusComponentProps>
>((props, ref) => {
  const { name = '', i18n } = props;
  const classes = useTrackingStatusPopoverStyles();
  const warningState = name !== healthyTrackingStatus;
  const displayName = trackingStatusMapping[name] ? i18n.getString(trackingStatusMapping[name]) : i18n.getString('not applicable');

  return (
    <div
      className="tracking-status-col"
      {...props}
      ref={ref}
    >
      <Badge
        appearance="tint"
        className={mergeClasses(classes.root, warningState && classes.warningState)}
      >
        {warningState && <WarningRegular style={{ marginRight: '2px' }} />}
        {displayName}
      </Badge>
    </div>
  );
});

interface LearnMoreComponentProps {
  i18n: I18n;
  config: ErrorCodeMapping;
  isCreationWorkflow: boolean;
  activityLogger?: {
    trace: (data: any) => void;
  };
  goalFixType: string
}

const LearnMoreComponent: React.FC<LearnMoreComponentProps> = ({
  i18n, config, activityLogger, goalFixType, isCreationWorkflow,
}) => {
  const { scope } = useContext(ScopeContext) as any;

  const onNavigation = () => {
    if (activityLogger && activityLogger.trace) {
      activityLogger.trace(`[GoalSelectorV3] navigate to: ${config.actionLink} based on ${goalFixType || config.title}`);
    }
    scope.history.push({
      pathname: getRoutePathById(allSiteMap, config.actionLink),
      search: scope.history.location.search,
    });
  };
  const confirmButtonText = (config && config.actionTitle) ? i18n.getString(config.actionTitle) : i18n.getString(_TL_('Confirm'));

  return (
    <>
      <NavigationPopup i18n={i18n} onNavigation={onNavigation} enabled={isCreationWorkflow} confirmButtonText={confirmButtonText} />
      <div>
        <LearnMoreLink
          linkText={i18n.getString(_TL_('Learn more about {{topic}}'), { topic: i18n.getString(config.helpText) })}
          topic={config.helpId}
          isGreyBackground={false}
          isUnderlineLink={false}
        />
      </div>
    </>
  );
};

LearnMoreComponent.defaultProps = {
  activityLogger: undefined,
};

interface TrackingStatusPopoverProps {
  i18n: I18n;
  goal: GoalType;
  isCreationWorkflow: boolean;
  activityLogger?: {
    trace: (data: any) => void;
  };
}

export const TrackingStatusPopover: React.FC<TrackingStatusPopoverProps> = ({
  i18n, goal, isCreationWorkflow, activityLogger,
}) => {
  const goalFixType = _.get(goal, ['GoalDiagnosticsInfo', 'GoalFixType'], '');
  const config = (goalFixType ? errorcodeMapping[goalFixType] : null) as ErrorCodeMapping;
  const trackingStatusConfig = (goal.TrackingStatus ? errorcodeMapping[goal.TrackingStatus] : null) as ErrorCodeMapping;
  const isOfflineGoal = goal.Type === 'OfflineConversionGoal';

  const params = useMemo(() => {
    const parameterPropertyName = errorcodeMapping[goalFixType] && errorcodeMapping[goalFixType].parameterPropertyName;
    // @ts-ignore
    let rawParams = _.get(goal, ['GoalDiagnosticsInfo', parameterPropertyName], {});
    Object.keys(rawParams).forEach((key) => {
      if (needLocalizedPropName[key] && localizedParameters[rawParams[key]]) {
        rawParams[key] = i18n.getString(localizedParameters[rawParams[key]]);
      }
    });
    if (goalFixType === GoalDiagnosticsInfoErrorCode.EventGoalInvalidParameter) {
      rawParams = generateEventGoalFixMessage(i18n, rawParams);
    }
    return rawParams;
  }, [goal, goalFixType, i18n]);

  const classes = useTrackingStatusPopoverStyles();
  const trackingStatusHelp =
      trackingStatusConfig && isOfflineGoal ? i18n.getString(_TL_('We recommend reviewing your offline goals and uploading conversions associated to this campaign.')) :
        trackingStatusConfig && i18n.getString(trackingStatusConfig.description);
  const defaultParameters = config && config.requiredParameters && config.requiredParameters.reduce((obj, param) => {
    const newObj = { ...obj };
    newObj[param] = 'N/A';
    return newObj;
  }, {});

  return (config || trackingStatusConfig) ? (
    <div className={classes.popover}>
      <Popover
        withArrow
        openOnHover
        mouseLeaveDelay={0}
        positioning="after"
      >
        <PopoverTrigger disableButtonEnhancement>
          <TrackingStatusComponent i18n={i18n} name={goal.TrackingStatus} />
        </PopoverTrigger>

        <PopoverSurface tabIndex={-1}>
          <div className={mergeClasses('ba-popover-content', classes.popoverContent)}>
            <span className={mergeClasses('ba-popover-title', classes.title)}>
              {config ? i18n.getString(config.title) : trackingStatusConfig && (i18n.getString(trackingStatusConfig.title))}
            </span>

            <div className="ba-popover-text">
              {config ? (
                <>
                  <p>{trackingStatusHelp}</p>
                  <p>
                    <strong>{i18n.getString(_TL_('Recommendation: '))}</strong>
                    {i18n.getString(config.description)}
                    {config.description2 && <span> {i18n.getString(config.description2, { ...defaultParameters, ...params }) }</span>}
                  </p>
                </>
              ) : (
                <p>{trackingStatusHelp}</p>
            )}
            </div>
            {(config && config.helpId) ?
          (<LearnMoreComponent i18n={i18n} config={config} isCreationWorkflow={isCreationWorkflow} activityLogger={activityLogger} goalFixType={goalFixType} />)
          :
          trackingStatusConfig && (<LearnMoreComponent i18n={i18n} config={trackingStatusConfig} isCreationWorkflow={isCreationWorkflow} activityLogger={activityLogger} goalFixType={goalFixType} />)
          }
          </div>
        </PopoverSurface>
      </Popover>
    </div>
  ) : <TrackingStatusComponent i18n={i18n} name={goal.TrackingStatus} />;
};

TrackingStatusPopover.defaultProps = {
  activityLogger: undefined,
};
